import { createTheme } from '@mui/material/styles';

import {
  brandAccent,
  color,
  extendFoundation,
  foundationPalette,
  foundationTypography,
} from './foundation';
import { withMobileComponents } from './mobileOverrides';

/**
 * Client portal theme. Also used, unchanged, by the partner portal, the public
 * `/order`, `/foreman-confirm/:token` and `/partner-offer/:token` pages (App.tsx).
 * Colours come from foundation.ts — none are declared here.
 */
export const clientTheme = createTheme({
  palette: foundationPalette(brandAccent),
  typography: {
    ...foundationTypography,
    // Fluid type: scales smoothly from 320 px → 1280 px viewport so long
    // FI/RU strings never break at a single breakpoint. See theme.ts.
    h1: {
      fontSize: 'clamp(1.375rem, 1.15rem + 1.1vw, 1.75rem)',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontSize: 'clamp(1.125rem, 1rem + 0.6vw, 1.375rem)',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: 'clamp(1rem, 0.95rem + 0.4vw, 1.125rem)',
      fontWeight: 600,
    },
    h4: {
      fontSize: 'clamp(1.5rem, 1.25rem + 1.2vw, 2rem)',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, color: color.textSecondary },
    body2: { ...foundationTypography.body2, color: color.textSecondary },
  },
  shape: { borderRadius: 12 },
  components: withMobileComponents(
    extendFoundation(color.brand, {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: color.bgDefault },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${color.cardBorder}`,
            boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${color.cardBorder}`,
            boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'transparent' },
        styleOverrides: {
          root: {
            backgroundColor: color.bgPaper,
            borderBottom: `1px solid ${color.cardBorder}`,
            color: color.textPrimary,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: color.bgPaper,
            borderRight: `1px solid ${color.cardBorder}`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10 },
          containedPrimary: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 8px',
            paddingTop: 8,
            paddingBottom: 8,
            '&.Mui-selected': {
              backgroundColor: color.brandSoft,
              color: color.brandText,
              '& .MuiListItemIcon-root': { color: color.brandText },
              '&:hover': { backgroundColor: color.brandSoft },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { minWidth: 36, color: color.textSecondary },
        },
      },
    }),
  ),
});
